import { useState, useEffect, useRef } from "react"
import { BellRing, CalendarClock, ClipboardList, Loader2, Mail } from "lucide-react"
import { MultiDropdown } from "@/components/ui/MultiDropdown"
import { useUserRole } from "@/hooks/useUserRole"
import { showSuccess, showError, formatApiError } from "@/lib/utils/notifications"
import { isCompanyDuplicateAddressCancelledError } from "@/features/company/utils/companyDuplicateAddress"
import { getFilterValueAngularStyle, mergeAngularFullFormParityFields } from "../../utils/companyValidators"
import apiClient from "@/lib/api/apiClient"
import { cn } from "@lib/utils/helpers"
import { COMM_FIELD_BASE, COMM_LABEL } from "@/lib/styles/companyEditTabShell"
import { SectionInfoIcon } from "@/components/ui/SectionInfoIcon"
import { SectionCard } from "@/components/ui/SectionCard"
import { ConfirmDialog } from "@/components/ui/ConfirmDialog"
import {
    companyEditConfirmPresentation,
    NOTICE_CONTACT_OPTIONS,
    REPORT_FREQUENCIES,
    REPORT_FREQ_VALUES,
    TOOLTIP_REMINDER_FIRST,
    TOOLTIP_REMINDER_SECOND,
    TOOLTIP_REPORTS_FREQUENCY,
    TOOLTIP_DEFAULT_UPDATE_NOTIFICATIONS,
    TOOLTIP_RESIDENT_PAID_INTRO,
    TOOLTIP_ACTIVE_DEFAULTS_INTRO,
} from "../../constants"
import tabStyles from "../../styles/companyInfoTab.module.css"

import type { CommunicationsTabProps, CompanyStaffMember } from "../../types"

/**
 * Angular `company-edit` options: WEEKLY | MONTHLY | NONE (`company-edit.component.html`).
 * API may send snake_case or camelCase (`emailFrequency`) or mixed case — normalize so `<select value>` matches.
 */
function normalizeReportEmailFrequency(raw: unknown): string {
    if (raw === null || raw === undefined) return ""
    const s = String(raw).trim()
    if (!s) return ""
    const u = s.toUpperCase()
    if (u === "WEEKLY" || u === "1" || u === "WEEK") return "WEEKLY"
    if (u === "MONTHLY" || u === "2" || u === "MONTH") return "MONTHLY"
    if (u === "NONE" || u === "0" || u === "FALSE" || u === "OFF") return "NONE"
    if (REPORT_FREQ_VALUES.has(u)) return u
    return u
}

function getEmailFrequencyFromCompany(company: Record<string, unknown> | null | undefined): string {
    if (!company) return "MONTHLY"
    const cfg = (company.company_config ?? company.companyConfig) as Record<string, unknown> | undefined
    const comm = (company.communications_info ?? company.communicationsInfo) as Record<string, unknown> | undefined
    const raw =
        company.email_frequency ??
        company.emailFrequency ??
        cfg?.email_frequency ??
        cfg?.emailFrequency ??
        comm?.email_frequency ??
        comm?.emailFrequency
    const n = normalizeReportEmailFrequency(raw)
    if (n && REPORT_FREQ_VALUES.has(n)) return n
    return "MONTHLY"
}

/** Angular `email_frequency`: `Validators.required` — only WEEKLY | MONTHLY | NONE (not empty placeholder). */
function isValidReportsReminderFrequency(value: unknown): boolean {
    const t = String(value ?? "").trim()
    return REPORT_FREQ_VALUES.has(t)
}

function pickDesignatedContact(company: Record<string, unknown> | null | undefined): string {
    const raw = company?.designated_contact ?? company?.designatedContact
    if (raw === null || raw === undefined || raw === "") return ""
    return String(raw)
}

function pickReminderField(company: Record<string, unknown> | null | undefined, snake: string, camel: string): string {
    const raw = company?.[snake] ?? company?.[camel]
    if (raw === null || raw === undefined) return ""
    return String(raw)
}

function parseCommaSeparatedField(val: string | string[] | null | undefined): string[] {
    if (!val) return []
    if (Array.isArray(val)) return val.map((v) => String(v).trim()).filter(Boolean)
    return val.split(",").map((v) => v.trim()).filter(Boolean)
}

function buildCommunicationsFormData(company: Record<string, unknown> | null | undefined) {
    const c = company ?? undefined
    return {
        email_frequency: getEmailFrequencyFromCompany(c),
        designated_contact: pickDesignatedContact(c),
        reminder_first: pickReminderField(c, "reminder_first", "reminderFirst"),
        first_notice_contact: parseCommaSeparatedField(
            (c?.first_notice_contact ?? c?.firstNoticeContact) as string | string[] | null | undefined
        ),
        reminder_second: pickReminderField(c, "reminder_second", "reminderSecond"),
        second_notice_contact: parseCommaSeparatedField(
            (c?.second_notice_contact ?? c?.secondNoticeContact) as string | string[] | null | undefined
        ),
        active_default_first_reminder: pickReminderField(c, "active_default_first_reminder", "activeDefaultFirstReminder"),
        active_default_first_notice_contact: parseCommaSeparatedField(
            (c?.active_default_first_notice_contact ?? c?.activeDefaultFirstNoticeContact) as
                | string
                | string[]
                | null
                | undefined
        ),
        active_default_second_reminder: pickReminderField(c, "active_default_second_reminder", "activeDefaultSecondReminder"),
        active_default_second_notice_contact: parseCommaSeparatedField(
            (c?.active_default_second_notice_contact ?? c?.activeDefaultSecondNoticeContact) as
                | string
                | string[]
                | null
                | undefined
        ),
        /** Angular `d-none` rows — still posted by `editCompany()` multiSelect. */
        reminder_third: pickReminderField(c, "reminder_third", "reminderThird"),
        third_notice_contact: parseCommaSeparatedField(
            (c?.third_notice_contact ?? c?.thirdNoticeContact) as string | string[] | null | undefined
        ),
        reminder_notice: pickReminderField(c, "reminder_notice", "reminderNotice"),
        notice_notice_contact: parseCommaSeparatedField(
            (c?.notice_notice_contact ?? c?.noticeNoticeContact) as string | string[] | null | undefined
        ),
        active_default_third_reminder: pickReminderField(
            c,
            "active_default_third_reminder",
            "activeDefaultThirdReminder"
        ),
        active_default_third_notice_contact: parseCommaSeparatedField(
            (c?.active_default_third_notice_contact ?? c?.activeDefaultThirdNoticeContact) as
                | string
                | string[]
                | null
                | undefined
        ),
    }
}

function dedupeStaffByUserId(list: CompanyStaffMember[]): CompanyStaffMember[] {
    const seen = new Set<number>()
    const out: CompanyStaffMember[] = []
    for (const item of list) {
        if (item.user_id == null) continue
        const uid = Number(item.user_id)
        if (Number.isNaN(uid) || seen.has(uid)) continue
        seen.add(uid)
        out.push(item)
    }
    return out
}

/**
 * Filter GET /api/property_staff?company_id=&roles=company for Designated Contact select options.
 * Handles nested data or flat array.
 */
function extractDesignatedContactStaffList(res: { data?: unknown }): CompanyStaffMember[] {
    const d = res?.data
    if (Array.isArray(d)) return d as CompanyStaffMember[]
    if (d && typeof d === "object") {
        const inner = (d as { data?: unknown }).data
        if (Array.isArray(inner)) return inner as CompanyStaffMember[]
        const staff = (d as { staff?: unknown }).staff
        if (Array.isArray(staff)) return staff as CompanyStaffMember[]
    }
    return []
}

const COMM_FIELD = cn(COMM_FIELD_BASE, "h-10 w-full")

export function CommunicationsTab({ companyId: companyIdProp, company, onUpdate }: CommunicationsTabProps) {
    const { hasPolicy } = useUserRole()
    const canEdit = hasPolicy(["company_communication_edit"])
    const companyId =
        companyIdProp?.trim() ||
        (company?.company_id != null && String(company.company_id)) ||
        (company?.id != null && String(company.id)) ||
        ""

    const [staffList, setStaffList] = useState<CompanyStaffMember[]>([])
    const [staffLoading, setStaffLoading] = useState(false)

    useEffect(() => {
        if (!companyId) return
        let cancelled = false
        setStaffLoading(true)
        ;(async () => {
            try {
                /** Angular `company-edit.loadStaffs()`: `GET /api/property_staff?company_id=&roles=company` only. */
                const res = await apiClient.get("/api/property_staff", {
                    params: { company_id: companyId, roles: "company" },
                })
                const list = dedupeStaffByUserId(extractDesignatedContactStaffList(res))
                if (!cancelled) setStaffList(list)
            } catch {
                if (!cancelled) setStaffList([])
            } finally {
                if (!cancelled) setStaffLoading(false)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [companyId])

    const [formData, setFormData] = useState(() =>
        buildCommunicationsFormData(company as Record<string, unknown> | null | undefined)
    )
    /** Mirrors Angular `ng-touched.ng-invalid` on `email_frequency` + failed submit. */
    const [reportsFreqTouched, setReportsFreqTouched] = useState(false)
    const [reportsFreqSubmitAttempted, setReportsFreqSubmitAttempted] = useState(false)
    /** Angular `contactSelected()` — Reminder when DESIGNATED_CONTACT picked with no designated contact. */
    const [designatedReminderOpen, setDesignatedReminderOpen] = useState(false)

    const prevCompanyRef = useRef(company)

    /** Re-sync when `company` from GET /api/company/:id updates (only on initial load, ID change, or post-save update). */
    useEffect(() => {
        if (!company) return
        const prevId = (prevCompanyRef.current as Record<string, unknown> | null | undefined)?.company_id
        const currId = (company as Record<string, unknown> | null | undefined)?.company_id
        const prevUpdated = (prevCompanyRef.current as Record<string, unknown> | null | undefined)?.updated_at
        const currUpdated = (company as Record<string, unknown> | null | undefined)?.updated_at

        if (!prevCompanyRef.current || prevId !== currId || prevUpdated !== currUpdated) {
            setFormData(buildCommunicationsFormData(company as Record<string, unknown> | null | undefined))
            setReportsFreqTouched(false)
            setReportsFreqSubmitAttempted(false)
        }
        prevCompanyRef.current = company
    }, [company])

    const onNoticeContactsChange = (
        key:
            | "first_notice_contact"
            | "second_notice_contact"
            | "active_default_first_notice_contact"
            | "active_default_second_notice_contact",
        vals: string[],
    ) => {
        setFormData((prev) => ({ ...prev, [key]: vals }))
        if (vals.includes("DESIGNATED_CONTACT") && !String(formData.designated_contact ?? "").trim()) {
            setDesignatedReminderOpen(true)
        }
    }
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!isValidReportsReminderFrequency(formData.email_frequency)) {
            setReportsFreqSubmitAttempted(true)
            showError("Reports Reminder Frequency is required.")
            return
        }
        try {
            /**
             * Angular `editCompany()`: `getFilterValue(form)` then `multiSelect()` on notice-contact arrays → FormData `key[0]`, …
             * Parity keys via `mergeAngularFullFormParityFields` (do not spread GET `company`).
             * DEV: `postCompanyUpdate` → `logCompanyPostPayloadDev` / `stableStringifyForAngularPostDiff` vs Angular Network POST.
             */
            const email_frequency = String(formData.email_frequency ?? "").trim()
            const tabFields: Record<string, unknown> = {
                email_frequency,
                designated_contact: String(formData.designated_contact ?? "").trim(),
                reminder_first: String(formData.reminder_first ?? "").trim(),
                reminder_second: String(formData.reminder_second ?? "").trim(),
                reminder_third: String(formData.reminder_third ?? "").trim(),
                reminder_notice: String(formData.reminder_notice ?? "").trim(),
                active_default_first_reminder: String(formData.active_default_first_reminder ?? "").trim(),
                active_default_second_reminder: String(formData.active_default_second_reminder ?? "").trim(),
                active_default_third_reminder: String(formData.active_default_third_reminder ?? "").trim(),
                first_notice_contact: [...formData.first_notice_contact],
                second_notice_contact: [...formData.second_notice_contact],
                third_notice_contact: [...formData.third_notice_contact],
                notice_notice_contact: [...formData.notice_notice_contact],
                active_default_first_notice_contact: [...formData.active_default_first_notice_contact],
                active_default_second_notice_contact: [...formData.active_default_second_notice_contact],
                active_default_third_notice_contact: [...formData.active_default_third_notice_contact],
            }
            const filtered = getFilterValueAngularStyle(tabFields) as Record<string, unknown>
            const payload = mergeAngularFullFormParityFields(company as Record<string, unknown> | undefined, filtered)

            await onUpdate(payload)
            showSuccess("Company Updated  Successfully!")
        } catch (err: unknown) {
            if (isCompanyDuplicateAddressCancelledError(err)) return
            showError(formatApiError(err))
        }
    }

    // Days 1 to 31
    const dayOptions = Array.from({ length: 31 }, (_, i) => String(i + 1))

    const reportsFreqInvalid = !isValidReportsReminderFrequency(formData.email_frequency)
    const reportsFreqErrorVisible =
        reportsFreqInvalid && (reportsFreqTouched || reportsFreqSubmitAttempted)

    const DaySelect = ({
        value,
        onChange,
        disabled,
        dataTestId,
    }: {
        value: string
        onChange: (v: string) => void
        disabled?: boolean
        dataTestId?: string
    }) => (
        <div className="flex w-28 shrink-0 self-stretch">
            <select
                data-testid={dataTestId}
                value={value}
                onChange={e => onChange(e.target.value)}
                disabled={disabled}
                className={cn(
                    COMM_FIELD_BASE,
                    "h-full min-h-10 w-full",
                    disabled && "cursor-not-allowed opacity-60",
                )}
            >
                <option value="">Select Send Date</option>
                <option value="none">No Reminder</option>
                {dayOptions.map(day => (
                    <option key={day} value={day}>{day}</option>
                ))}
            </select>
        </div>
    )

    return (
        <form
            data-testid="company-communications-tab-form"
            onSubmit={handleSubmit}
            data-tab-id="communications"
            className={tabStyles.tabContent}
        >
            <SectionCard
                icon={CalendarClock}
                title="Reports Reminder Frequency"
                testId="company-communications-reports-frequency-card"
                infoContent={TOOLTIP_REPORTS_FREQUENCY}
                infoTestId="company-communications-reports-note"
            >
                <div className="w-full max-w-xs sm:max-w-sm">
                    <label htmlFor="email_frequency" className={COMM_LABEL}>
                        Frequency<span className="text-[#dc2626] font-semibold">*</span>
                    </label>
                    <select
                        id="email_frequency"
                        data-testid="company-communications-email-frequency-select"
                        value={formData.email_frequency}
                        onChange={e => {
                            const v = e.target.value
                            setFormData(prev => ({ ...prev, email_frequency: v }))
                            if (isValidReportsReminderFrequency(v)) {
                                setReportsFreqSubmitAttempted(false)
                            }
                        }}
                        onBlur={() => setReportsFreqTouched(true)}
                        disabled={!canEdit}
                        aria-invalid={reportsFreqErrorVisible}
                        className={cn(
                            COMM_FIELD,
                            "w-full",
                            !canEdit && "cursor-not-allowed opacity-60",
                            reportsFreqErrorVisible
                                ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/25"
                                : "border-slate-200"
                        )}
                    >
                        <option value="">Select Frequency</option>
                        {REPORT_FREQUENCIES.map(opt => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
            </SectionCard>

            <SectionCard
                icon={BellRing}
                title="Default Update Notification Preferences"
                testId="company-communications-default-notification-block"
                infoContent={TOOLTIP_DEFAULT_UPDATE_NOTIFICATIONS}
                infoTestId="company-communications-default-notifications-note"
            >
                <div className="w-full min-w-0 max-w-md">
                    <label
                        className={COMM_LABEL}
                        htmlFor="company-communications-designated-contact-select"
                    >
                        Designated Company Contact
                    </label>
                    <select
                        id="company-communications-designated-contact-select"
                        data-testid="company-communications-designated-contact-select"
                        value={formData.designated_contact}
                        onChange={e => setFormData(prev => ({ ...prev, designated_contact: e.target.value }))}
                        disabled={!canEdit || staffLoading}
                        className={cn(COMM_FIELD, (!canEdit || staffLoading) && "cursor-not-allowed opacity-60")}
                    >
                        <option value="">Select Contact</option>
                        {staffList.map(staff => (
                            <option key={staff.user_id} value={String(staff.user_id ?? "")}>
                                {staff.name ||
                                    `${staff.first_name || ""} ${staff.last_name || ""}`.trim() ||
                                    "Unknown"}
                            </option>
                        ))}
                    </select>
                    {staffLoading ? (
                        <p className="mt-2 flex items-center gap-1.5 text-xs text-[rgb(21_151_255)]">
                            <Loader2 size={12} className="animate-spin" aria-hidden />
                            Loading contacts…
                        </p>
                    ) : null}
                </div>
            </SectionCard>

            <SectionCard
                icon={Mail}
                title="Resident paid reminder / warning email"
                testId="company-communications-resident-paid-block"
                infoContent={TOOLTIP_RESIDENT_PAID_INTRO}
                infoTestId="company-communications-resident-paid-note"
            >
                <div className="grid w-full min-w-0 grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
                    <div className="min-w-0">
                        <span className={COMM_LABEL}>
                            First Reminder
                            <SectionInfoIcon
                                content={TOOLTIP_REMINDER_FIRST}
                                data-testid="company-communications-info-resident-paid-first-reminder"
                            />
                        </span>
                        <div className="mt-1.5 flex w-full min-w-0 flex-row items-stretch gap-3">
                            <DaySelect
                                dataTestId="company-communications-reminder-first-day-select"
                                value={formData.reminder_first}
                                onChange={v => setFormData(prev => ({ ...prev, reminder_first: v }))}
                                disabled={!canEdit}
                            />
                            <div className="min-w-0 flex-1">
                                <MultiDropdown
                                    chipRemoveTestIdPrefix="company-communications-resident-first-notice-chip-remove"
                                    triggerDataTestId="company-communications-first-notice-contacts-trigger"
                                    options={NOTICE_CONTACT_OPTIONS}
                                    selected={formData.first_notice_contact}
                                    onChange={(vals) => onNoticeContactsChange("first_notice_contact", vals)}
                                    placeholder="Notice contacts"
                                    disabled={!canEdit}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="min-w-0">
                        <span className={COMM_LABEL}>
                            Second Reminder
                            <SectionInfoIcon
                                content={TOOLTIP_REMINDER_SECOND}
                                data-testid="company-communications-info-resident-paid-second-reminder"
                            />
                        </span>
                        <div className="mt-1.5 flex w-full min-w-0 flex-row items-stretch gap-3">
                            <DaySelect
                                dataTestId="company-communications-reminder-second-day-select"
                                value={formData.reminder_second}
                                onChange={v => setFormData(prev => ({ ...prev, reminder_second: v }))}
                                disabled={!canEdit}
                            />
                            <div className="min-w-0 flex-1">
                                <MultiDropdown
                                    chipRemoveTestIdPrefix="company-communications-resident-second-notice-chip-remove"
                                    triggerDataTestId="company-communications-second-notice-contacts-trigger"
                                    options={NOTICE_CONTACT_OPTIONS}
                                    selected={formData.second_notice_contact}
                                    onChange={(vals) => onNoticeContactsChange("second_notice_contact", vals)}
                                    placeholder="Notice contacts"
                                    disabled={!canEdit}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </SectionCard>

            <SectionCard
                icon={ClipboardList}
                title="List of active defaults — email preferences"
                testId="company-communications-active-defaults-block"
                infoContent={TOOLTIP_ACTIVE_DEFAULTS_INTRO}
                infoTestId="company-communications-active-defaults-note"
            >
                <div className="grid w-full min-w-0 grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
                    <div className="min-w-0">
                        <span className={COMM_LABEL}>
                            First Reminder
                            <SectionInfoIcon
                                content={TOOLTIP_REMINDER_FIRST}
                                data-testid="company-communications-info-active-default-first-reminder"
                            />
                        </span>
                        <div className="mt-1.5 flex w-full min-w-0 flex-row items-stretch gap-3">
                            <DaySelect
                                dataTestId="company-communications-active-default-first-day-select"
                                value={formData.active_default_first_reminder}
                                onChange={v => setFormData(prev => ({ ...prev, active_default_first_reminder: v }))}
                                disabled={!canEdit}
                            />
                            <div className="min-w-0 flex-1">
                                <MultiDropdown
                                    chipRemoveTestIdPrefix="company-communications-active-default-first-notice-chip-remove"
                                    triggerDataTestId="company-communications-active-default-first-notice-contacts-trigger"
                                    options={NOTICE_CONTACT_OPTIONS}
                                    selected={formData.active_default_first_notice_contact}
                                    onChange={(vals) =>
                                        onNoticeContactsChange("active_default_first_notice_contact", vals)
                                    }
                                    placeholder="Notice contacts"
                                    disabled={!canEdit}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="min-w-0">
                        <span className={COMM_LABEL}>
                            Second Reminder
                            <SectionInfoIcon
                                content={TOOLTIP_REMINDER_SECOND}
                                data-testid="company-communications-info-active-default-second-reminder"
                            />
                        </span>
                        <div className="mt-1.5 flex w-full min-w-0 flex-row items-stretch gap-3">
                            <DaySelect
                                dataTestId="company-communications-active-default-second-day-select"
                                value={formData.active_default_second_reminder}
                                onChange={v => setFormData(prev => ({ ...prev, active_default_second_reminder: v }))}
                                disabled={!canEdit}
                            />
                            <div className="min-w-0 flex-1">
                                <MultiDropdown
                                    chipRemoveTestIdPrefix="company-communications-active-default-second-notice-chip-remove"
                                    triggerDataTestId="company-communications-active-default-second-notice-contacts-trigger"
                                    options={NOTICE_CONTACT_OPTIONS}
                                    selected={formData.active_default_second_notice_contact}
                                    onChange={(vals) =>
                                        onNoticeContactsChange("active_default_second_notice_contact", vals)
                                    }
                                    placeholder="Notice contacts"
                                    disabled={!canEdit}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </SectionCard>

            {/* Hidden submit button triggered by CompanyEdit main form header */}
            <button
                type="submit"
                id="communications-hidden-submit"
                data-testid="company-communications-hidden-submit"
                style={{ display: "none" }}
            />

            {/* Angular `contactSelected()` Reminder dialog */}
            <ConfirmDialog
                isOpen={designatedReminderOpen}
                title="Reminder"
                message="Please identify a specific Designated Company contact"
                primaryLabel="Ok"
                onPrimary={() => setDesignatedReminderOpen(false)}
                secondaryLabel=""
                onSecondary={() => setDesignatedReminderOpen(false)}
                {...companyEditConfirmPresentation}
                overlayDataTestId="company-communications-designated-reminder-overlay"
                primaryButtonDataTestId="company-communications-designated-reminder-ok-button"
                secondaryButtonDataTestId="company-communications-designated-reminder-secondary-button"
                closeButtonDataTestId="company-communications-designated-reminder-close-button"
            />
        </form>
    )
}
